import { decimalCompare, type DecimalValue } from "../values/decimal";
import { ORDER_DIRECTIONS, ORDER_STATUSES, type OrderDirection, type OrderStatus } from "./order-types";
import { isMissingOrderPrice } from "./order-money";
import { ORDER_PRICE_FILTER_ENABLED } from "./order-price-filter-config";
import { ORDER_VIEW_LABELS } from "./order-ledger-copy";

export type OrderViewFacts = { status: OrderStatus; direction: OrderDirection; balanceDue?: DecimalValue | null; pricePerItem?: number | string | null };

export const FULFILLED_BALANCE_DUE_LABEL = ORDER_VIEW_LABELS.fulfilledBalanceDue;
export const OPEN_ORDERS_LABEL = ORDER_VIEW_LABELS.openSales;
export const MISSING_PRICE_VIEW = "missingPrice";

type OrderViewDefinition = {
  value: string;
  label: string;
  matches: (order: OrderViewFacts) => boolean;
  home: boolean;
  financial: boolean;
};

const FINISHED_STATUSES: readonly OrderStatus[] = [ORDER_STATUSES.fulfilled, ORDER_STATUSES.cancelled];

export function isPendingOrder(order: OrderViewFacts): boolean {
  return !FINISHED_STATUSES.includes(order.status);
}

function isOpenSale(order: OrderViewFacts): boolean {
  return order.status === ORDER_STATUSES.ordered && order.direction === ORDER_DIRECTIONS.sale;
}

export function isCancelledOrder({ status }: Pick<OrderViewFacts, "status">): boolean {
  return status === ORDER_STATUSES.cancelled;
}

export function isFulfilledWithBalanceDue({ status, balanceDue }: Pick<OrderViewFacts, "status" | "balanceDue">): boolean {
  return status === ORDER_STATUSES.fulfilled && balanceDue != null && decimalCompare(balanceDue, 0) > 0;
}

function hasMissingPrice(order: OrderViewFacts): boolean {
  return isMissingOrderPrice(order.pricePerItem);
}

export const ORDER_VIEWS = [
  { value: "pending", label: ORDER_VIEW_LABELS.pending, matches: isPendingOrder, home: true, financial: false },
  { value: "fulfilled", label: ORDER_VIEW_LABELS.fulfilled, matches: (order) => order.status === ORDER_STATUSES.fulfilled, home: true, financial: false },
  { value: "fulfilledBalanceDue", label: FULFILLED_BALANCE_DUE_LABEL, matches: isFulfilledWithBalanceDue, home: false, financial: true },
  { value: "cancelled", label: ORDER_VIEW_LABELS.cancelled, matches: isCancelledOrder, home: true, financial: false },
  { value: "all", label: ORDER_VIEW_LABELS.all, matches: () => true, home: false, financial: false },
  { value: "openSales", label: OPEN_ORDERS_LABEL, matches: isOpenSale, home: false, financial: false },
  { value: MISSING_PRICE_VIEW, label: ORDER_VIEW_LABELS.missingPrice, matches: hasMissingPrice, home: false, financial: true },
] as const satisfies readonly OrderViewDefinition[];

export type OrderView = (typeof ORDER_VIEWS)[number]["value"];

export const DEFAULT_ORDER_VIEW: OrderView = "pending";

function viewDefinition(view: OrderView): OrderViewDefinition {
  return ORDER_VIEWS.find((definition) => definition.value === view)!;
}

export function orderMatchesView(order: OrderViewFacts, view: OrderView): boolean {
  return viewDefinition(view).matches(order);
}

export function orderViewLabel(view: OrderView): string {
  return viewDefinition(view).label;
}

export function orderViewsFor(canViewMoney: boolean): Array<(typeof ORDER_VIEWS)[number]> {
  return ORDER_VIEWS.filter(availableView.bind(null, canViewMoney));
}

function availableView(canViewMoney: boolean, view: OrderViewDefinition): boolean {
  return (canViewMoney || !view.financial) && (view.value !== MISSING_PRICE_VIEW || ORDER_PRICE_FILTER_ENABLED);
}

export function homeViewOf(order: OrderViewFacts): OrderView {
  return ORDER_VIEWS.find((view) => view.home && view.matches(order))!.value;
}
